import React, { useEffect, useState, useCallback } from 'react';
import { usersApi } from '../api/client';
import type { User, UserStats } from '../api/client';
import './Users.css';

const Users: React.FC = () => {
  const [users, setUsers] = useState<User[]>([]);
  const [stats, setStats] = useState<UserStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingUser, setEditingUser] = useState<User | null>(null);
  const [filterRole, setFilterRole] = useState<string>('all');
  const [filterStatus, setFilterStatus] = useState<string>('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [newUser, setNewUser] = useState({
    username: '',
    email: '',
    firstName: '',
    lastName: '',
    displayName: '',
    role: 'user' as 'user' | 'admin' | 'moderator',
    status: 'active' as 'active' | 'inactive' | 'suspended' | 'pending'
  });

  const fetchUsers = useCallback(async () => {
    try {
      setLoading(true);
      const response = await usersApi.getUsers({
        role: filterRole !== 'all' ? filterRole : undefined,
        status: filterStatus !== 'all' ? filterStatus : undefined,
        search: searchTerm || undefined
      });
      setUsers(response.data);
    } catch (err) {
      setError('Failed to load users');
      console.error('Users error:', err);
    } finally {
      setLoading(false);
    }
  }, [filterRole, filterStatus, searchTerm]);

  const fetchUserStats = async () => {
    try {
      const response = await usersApi.getUserStats();
      setStats(response.data);
    } catch (err) {
      console.error('Failed to load user stats:', err);
    }
  };

  useEffect(() => {
    fetchUsers();
    fetchUserStats();
  }, [fetchUsers]);

  const handleAddUser = async () => {
    if (!newUser.username.trim() || !newUser.email.trim() || !newUser.firstName.trim()) return;

    try {
      const userData = {
        username: newUser.username,
        email: newUser.email.toLowerCase(),
        firstName: newUser.firstName,
        lastName: newUser.lastName || '',
        displayName: newUser.displayName || `${newUser.firstName} ${newUser.lastName}`.trim(),
        role: newUser.role,
        status: newUser.status,
        profile: {
          bio: '',
          location: '',
          timezone: 'America/New_York',
          language: 'en',
          theme: 'light'
        },
        preferences: {
          notifications: {
            email: true,
            push: false,
            sms: false
          },
          privacy: {
            profileVisibility: 'private' as const,
            shareData: false
          },
          dashboard: {
            defaultView: 'overview',
            showStats: true,
            compactMode: false
          }
        },
        metadata: {
          source: 'admin-created',
          tags: [],
          notes: ''
        }
      };

      await usersApi.createUser(userData);
      setNewUser({
        username: '',
        email: '',
        firstName: '',
        lastName: '',
        displayName: '',
        role: 'user',
        status: 'active'
      });
      setShowAddModal(false);
      fetchUsers();
      fetchUserStats();
    } catch (err) {
      console.error('Error creating user:', err);
      setError('Failed to create user');
    }
  };

  const handleEditUser = (user: User) => {
    setEditingUser(user);
    setShowEditModal(true);
  };

  const handleUpdateUser = async () => {
    if (!editingUser) return;

    try {
      await usersApi.updateUser(editingUser.id, {
        firstName: editingUser.firstName,
        lastName: editingUser.lastName,
        displayName: editingUser.displayName,
        email: editingUser.email.toLowerCase(),
        role: editingUser.role,
        status: editingUser.status,
        profile: editingUser.profile
      });
      setShowEditModal(false);
      setEditingUser(null);
      fetchUsers();
      fetchUserStats();
    } catch (err) {
      console.error('Error updating user:', err);
      setError('Failed to update user');
    }
  };

  const handleDeleteUser = async (userId: string) => {
    if (!window.confirm('Are you sure you want to delete this user?')) return;

    try {
      await usersApi.deleteUser(userId);
      fetchUsers();
      fetchUserStats();
    } catch (err) {
      console.error('Error deleting user:', err);
      setError('Failed to delete user');
    }
  };

  const filteredUsers = users.filter(user => {
    const matchesRole = filterRole === 'all' || user.role === filterRole;
    const matchesStatus = filterStatus === 'all' || user.status === filterStatus;
    const matchesSearch = searchTerm === '' || 
      user.username.toLowerCase().includes(searchTerm.toLowerCase()) ||
      user.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
      user.firstName.toLowerCase().includes(searchTerm.toLowerCase()) ||
      user.lastName.toLowerCase().includes(searchTerm.toLowerCase()) ||
      user.displayName.toLowerCase().includes(searchTerm.toLowerCase());
    
    return matchesRole && matchesStatus && matchesSearch;
  });

  if (loading) return <div className="loading">Loading users...</div>;
  if (error) return <div className="error">Error: {error}</div>;

  return (
    <div className="users-container">
      <div className="users-header">
        <h1>User Management</h1>
        <button className="add-user-btn" onClick={() => setShowAddModal(true)}>
          Add User
        </button>
      </div>

      {stats && (
        <div className="user-stats">
          <div className="stat-card">
            <h3>Total Users</h3>
            <p className="stat-number">{stats.total}</p>
          </div>
          <div className="stat-card">
            <h3>Active Users</h3>
            <p className="stat-number">{stats.active}</p>
          </div>
          <div className="stat-card">
            <h3>Admins</h3>
            <p className="stat-number">{stats.admins}</p>
          </div>
          <div className="stat-card">
            <h3>Recently Active</h3>
            <p className="stat-number">{stats.recentlyActive}</p>
          </div>
        </div>
      )}

      <div className="users-filters">
        <input
          type="text"
          placeholder="Search users..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="search-input"
        />
        
        <select 
          value={filterRole} 
          onChange={(e) => setFilterRole(e.target.value)}
          className="filter-select"
        >
          <option value="all">All Roles</option>
          <option value="user">Users</option>
          <option value="admin">Admins</option>
          <option value="moderator">Moderators</option>
        </select>

        <select 
          value={filterStatus} 
          onChange={(e) => setFilterStatus(e.target.value)}
          className="filter-select"
        >
          <option value="all">All Statuses</option>
          <option value="active">Active</option>
          <option value="inactive">Inactive</option>
          <option value="suspended">Suspended</option>
          <option value="pending">Pending</option>
        </select>
      </div>

      <div className="users-grid">
        {filteredUsers.length === 0 ? (
          <div className="no-users">No users found matching your criteria</div>
        ) : (
          filteredUsers.map(user => (
            <div key={user.id} className="user-card">
              <div className="user-header">
                <div>
                  <h3 className="user-name">{user.displayName}</h3>
                  <p className="user-username">@{user.username}</p>
                </div>
                <div className="user-actions">
                  <button onClick={() => handleEditUser(user)} className="edit-btn">
                    Edit
                  </button>
                  <button 
                    onClick={() => handleDeleteUser(user.id)} 
                    className="delete-btn"
                    disabled={user.id === '550e8400-e29b-41d4-a716-446655440000'}
                  >
                    Delete
                  </button>
                </div>
              </div>
              
              <div className="user-details">
                <p><strong>Email:</strong> {user.email}</p>
                <p><strong>Role:</strong> <span className={`role-badge ${user.role}`}>{user.role}</span></p>
                <p><strong>Status:</strong> <span className={`status-badge ${user.status}`}>{user.status}</span></p>
                <p><strong>Location:</strong> {user.profile.location || 'Not specified'}</p>
                <p><strong>Last Active:</strong> {new Date(user.lastActiveAt).toLocaleDateString()}</p>
                <p><strong>Login Count:</strong> {user.security.loginCount}</p>
              </div>

              {user.metadata.tags.length > 0 && (
                <div className="user-tags">
                  {user.metadata.tags.map((tag, index) => (
                    <span key={index} className="tag">{tag}</span>
                  ))}
                </div>
              )}
            </div>
          ))
        )}
      </div>

      {showAddModal && (
        <div className="modal-overlay" onClick={() => setShowAddModal(false)}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Add New User</h2>
              <button className="close-btn" onClick={() => setShowAddModal(false)}>×</button>
            </div>
            
            <div className="modal-content">
              <div className="form-group">
                <label>Username *</label>
                <input
                  type="text"
                  value={newUser.username}
                  onChange={(e) => setNewUser({...newUser, username: e.target.value})}
                  placeholder="Enter username"
                />
              </div>

              <div className="form-group">
                <label>Email *</label>
                <input
                  type="email"
                  value={newUser.email}
                  onChange={(e) => setNewUser({...newUser, email: e.target.value})}
                  placeholder="Enter email address"
                />
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>First Name *</label>
                  <input
                    type="text"
                    value={newUser.firstName}
                    onChange={(e) => setNewUser({...newUser, firstName: e.target.value})}
                    placeholder="First name"
                  />
                </div>

                <div className="form-group">
                  <label>Last Name</label>
                  <input
                    type="text"
                    value={newUser.lastName}
                    onChange={(e) => setNewUser({...newUser, lastName: e.target.value})}
                    placeholder="Last name"
                  />
                </div>
              </div>

              <div className="form-group">
                <label>Display Name</label>
                <input
                  type="text"
                  value={newUser.displayName}
                  onChange={(e) => setNewUser({...newUser, displayName: e.target.value})}
                  placeholder="Display name (auto-generated if empty)"
                />
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>Role</label>
                  <select
                    value={newUser.role}
                    onChange={(e) => setNewUser({...newUser, role: e.target.value as any})}
                  >
                    <option value="user">User</option>
                    <option value="moderator">Moderator</option>
                    <option value="admin">Admin</option>
                  </select>
                </div>

                <div className="form-group">
                  <label>Status</label>
                  <select
                    value={newUser.status}
                    onChange={(e) => setNewUser({...newUser, status: e.target.value as any})}
                  >
                    <option value="active">Active</option>
                    <option value="inactive">Inactive</option>
                    <option value="pending">Pending</option>
                    <option value="suspended">Suspended</option>
                  </select>
                </div>
              </div>
            </div>

            <div className="modal-footer">
              <button onClick={() => setShowAddModal(false)} className="cancel-btn">
                Cancel
              </button>
              <button onClick={handleAddUser} className="save-btn">
                Create User
              </button>
            </div>
          </div>
        </div>
      )}

      {showEditModal && editingUser && (
        <div className="modal-overlay" onClick={() => setShowEditModal(false)}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Edit User</h2>
              <button className="close-btn" onClick={() => setShowEditModal(false)}>×</button>
            </div>
            
            <div className="modal-content">
              <div className="form-group">
                <label>Username</label>
                <input
                  type="text"
                  value={editingUser.username}
                  onChange={(e) => setEditingUser({...editingUser, username: e.target.value})}
                  placeholder="Enter username"
                />
              </div>

              <div className="form-group">
                <label>Email</label>
                <input
                  type="email"
                  value={editingUser.email}
                  onChange={(e) => setEditingUser({...editingUser, email: e.target.value})}
                  placeholder="Enter email address"
                />
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>First Name</label>
                  <input
                    type="text"
                    value={editingUser.firstName}
                    onChange={(e) => setEditingUser({...editingUser, firstName: e.target.value})}
                    placeholder="First name"
                  />
                </div>

                <div className="form-group">
                  <label>Last Name</label>
                  <input
                    type="text"
                    value={editingUser.lastName}
                    onChange={(e) => setEditingUser({...editingUser, lastName: e.target.value})}
                    placeholder="Last name"
                  />
                </div>
              </div>

              <div className="form-group">
                <label>Display Name</label>
                <input
                  type="text"
                  value={editingUser.displayName}
                  onChange={(e) => setEditingUser({...editingUser, displayName: e.target.value})}
                  placeholder="Display name"
                />
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>Role</label>
                  <select
                    value={editingUser.role}
                    onChange={(e) => setEditingUser({...editingUser, role: e.target.value as any})}
                  >
                    <option value="user">User</option>
                    <option value="moderator">Moderator</option>
                    <option value="admin">Admin</option>
                  </select>
                </div>

                <div className="form-group">
                  <label>Status</label>
                  <select
                    value={editingUser.status}
                    onChange={(e) => setEditingUser({...editingUser, status: e.target.value as any})}
                  >
                    <option value="active">Active</option>
                    <option value="inactive">Inactive</option>
                    <option value="pending">Pending</option>
                    <option value="suspended">Suspended</option>
                  </select>
                </div>
              </div>

              <div className="form-group">
                <label>Bio</label>
                <textarea
                  value={editingUser.profile.bio}
                  onChange={(e) => setEditingUser({
                    ...editingUser, 
                    profile: {...editingUser.profile, bio: e.target.value}
                  })}
                  placeholder="User bio"
                  rows={3}
                />
              </div>

              <div className="form-group">
                <label>Location</label>
                <input
                  type="text"
                  value={editingUser.profile.location}
                  onChange={(e) => setEditingUser({
                    ...editingUser, 
                    profile: {...editingUser.profile, location: e.target.value}
                  })}
                  placeholder="Location"
                />
              </div>
            </div>

            <div className="modal-footer">
              <button onClick={() => setShowEditModal(false)} className="cancel-btn">
                Cancel
              </button>
              <button onClick={handleUpdateUser} className="save-btn">
                Update User
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Users;